import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { testimonialsData } from '../data/testimonials';
import { Star, ChevronLeft, ChevronRight, Quote, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Flag } from './Flag';
import { countryName } from '../lib/countries';


const ALL_REVIEWS_LABEL = {
  es: 'Ver todas las opiniones',
  en: 'Read all reviews',
  de: 'Alle Bewertungen lesen',
};

export const Testimonials = () => {
  const { t, language } = useLanguage();
  const [published, setPublished] = useState([]);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  // Reseñas publicadas desde el panel (las más recientes primero)
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/public`)
      .then(({ data }) => setPublished(Array.isArray(data) ? data : []))
      .catch(() => setPublished([]));
  }, []);

  // Publicadas + las históricas de la web, sin duplicados
  const reviews = useMemo(() => {
    const mapped = published.map((r) => ({
      name: r.name,
      country: r.country,
      countryCode: (r.country_code || '').toUpperCase(),
      text: r.text,
      rating: r.rating || 5,
    }));
    const names = new Set(mapped.map((r) => (r.name || '').toLowerCase()));
    return [...mapped, ...testimonialsData.filter((r) => !names.has(r.name.toLowerCase()))];
  }, [published]);


  const nextTestimonial = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevTestimonial = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  useEffect(() => {
    const interval = setInterval(nextTestimonial, 9000);
    return () => clearInterval(interval);
  }, []);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <section id="testimonials" className="py-24 bg-gradient-to-b from-white to-[#E8F5FA]/50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23e07b54' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }} />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">{t.testimonials.subtitle}</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mt-4 mb-6">
            {t.testimonials.title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mx-auto" />
          {t.testimonials.lead && (
            <p className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl mx-auto" data-testid="testimonials-lead">
              {t.testimonials.lead}
            </p>
          )}
        </motion.div>

        {/* Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              className="relative"
            >
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-100 relative overflow-hidden">
                {/* Quote Icon */}
                <div className="absolute top-8 right-8 opacity-10">
                  <Quote className="w-24 h-24 text-[#A8D8EA]" />
                </div>

                {/* Stars */}
                <div className="flex justify-center mb-6">
                  {[...Array(reviews[currentIndex].rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8 text-center italic relative z-10">
                  "{reviews[currentIndex].text}"
                </p>

                {/* Author Info */}
                <div className="flex items-center justify-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#A8D8EA] to-[#7DBDD9] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {reviews[currentIndex].name[0]}
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-[#4A4A4A] text-lg">{reviews[currentIndex].name}</p>
                    <p className="text-gray-600 flex items-center space-x-2">
                      <Flag code={reviews[currentIndex].countryCode} title={reviews[currentIndex].country} className="!w-7 !h-5" />
                      <span>{countryName(reviews[currentIndex].countryCode?.toLowerCase(), language) || reviews[currentIndex].country}</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <button
            onClick={prevTestimonial}
            data-testid="testimonial-prev"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-16 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#E8F5FA] transition-colors duration-200 group"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600 group-hover:text-[#7DBDD9]" />
          </button>
          <button
            onClick={nextTestimonial}
            data-testid="testimonial-next"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-16 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#E8F5FA] transition-colors duration-200 group"
          >
            <ChevronRight className="w-6 h-6 text-gray-600 group-hover:text-[#7DBDD9]" />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                data-testid={`testimonial-dot-${index}`}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-[#A8D8EA] w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Grid of all testimonials (below carousel) */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {reviews.slice(0, 6).map((testimonial, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-sm text-gray-700 mb-4 line-clamp-3">"{testimonial.text}"</p>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-gradient-to-br from-[#A8D8EA] to-[#7DBDD9] rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {testimonial.name[0]}
                </div>
                <div>
                  <p className="font-semibold text-[#4A4A4A] text-sm">{testimonial.name}</p>
                  <p className="text-xs text-gray-600">
                    <Flag code={testimonial.countryCode} title={testimonial.country} className="mr-1.5" />{countryName(testimonial.countryCode?.toLowerCase(), language) || testimonial.country}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="text-center mt-10">
          <Link
            to="/opiniones"
            data-testid="all-reviews-link"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#7DBDD9] font-semibold border border-[#A8D8EA] hover:bg-[#E8F5FA] transition-colors shadow-sm"
          >
            {ALL_REVIEWS_LABEL[language] || ALL_REVIEWS_LABEL.es}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};